import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PsCheckboxTuner, PsModalTuner } from 'stencilSetup';
import { ResponseDto, ErrorResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import {
  POST_EMAIL_CONFIRMATION_KEY,
  SendConfirmationEmailRequestDto,
} from './utils';
import { useSubscriberQuery } from 'theSource/queries';
import { ROUTES } from 'enrollment/app/routes';
import { getPlanYearLookupCodeFromOrgPlanYear } from 'components/DownloadConfirmation/utils';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';

interface EmailConfirmationModalProps {
  enrollmentContext?: string;
  planYearStartsOn: Date;
  planYearEndsOn: Date;
}

export const EmailConfirmationModal = ({
  enrollmentContext,
  planYearStartsOn,
  planYearEndsOn,
}: EmailConfirmationModalProps) => {
  const { t } = useTranslation('messages.emailConfirmation');
  const navigate = useNavigate();
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<(string | null)[]>([
    null,
    null,
  ]);
  const toast = useToastDialog();

  const { subscriber } = useSubscriberQuery();
  const canUpdateEmail =
    (subscriber?.data.email?.value !== null &&
      subscriber?.data.email?.metadata.update) ||
    (subscriber?.data.email2?.value !== null &&
      subscriber?.data.email2?.metadata.update);
  const showPrimaryEmail =
    subscriber?.data.email?.value !== null &&
    String(subscriber?.data.email?.value).length > 0;
  const showSecondaryEmail =
    subscriber?.data.email2?.value !== null &&
    String(subscriber?.data.email2?.value).length > 0;
  const hasValidEmail = showPrimaryEmail || showSecondaryEmail;
  const enableSubmit =
    emailRecipients.some((recipient) => recipient !== null) || !hasValidEmail;

  const orgPlanYearCode = getPlanYearLookupCodeFromOrgPlanYear(
    planYearStartsOn,
    planYearEndsOn
  );

  const { mutate: sendConfirmationEmail } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    SendConfirmationEmailRequestDto
  >({
    queryKey: POST_EMAIL_CONFIRMATION_KEY,
    method: 'post',
    endpoint: '/enrollment/confirm/send_emails',
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
    toastRef: toast
  });

  const updateEmailRecipients = (e: any) => {
    if (e.target.tagName !== 'INPUT') {
      return;
    }
    const index = e.target.id === 'primary-email' ? 0 : 1;
    setEmailRecipients((prev) => {
      const newRecipients = [...prev];
      newRecipients[index] =
        newRecipients[index] === null ? e.target.name : null;
      return newRecipients;
    });
  };

  const renderModalBody = () => {
    return (
      <div>
        {isEmailSent ? (
          <div className="body1">
            {t('bodySent')}
            {emailRecipients.map((recipient) => {
              return (
                recipient && (
                  <div key={recipient} className="body1 semiBold">
                    {recipient}
                  </div>
                )
              );
            })}
          </div>
        ) : (
          <div className="body1">
            {t('bodySelect')}
            {showPrimaryEmail && (
              <div className="d-flex align-items-center">
                <PsCheckboxTuner
                  inputId="primary-email"
                  label={t('primaryEmail')}
                  name={String(subscriber?.data.email?.value)}
                  onClick={updateEmailRecipients}
                />
                <div className="body1 mb-0 pl-3 mt-2_5">
                  {String(
                    subscriber?.data.email?.value || t('noEmailProvided')
                  )}
                </div>
              </div>
            )}
            {showSecondaryEmail && (
              <div className="d-flex align-items-center">
                <PsCheckboxTuner
                  inputId="secondary-email"
                  label={t('secondaryEmail')}
                  name={String(subscriber?.data.email2?.value)}
                  onClick={updateEmailRecipients}
                />
                <div className="body1 mb-0 pl-3 mt-2_5">
                  {String(subscriber?.data.email2?.value)}
                </div>
              </div>
            )}
            {!showPrimaryEmail && !showSecondaryEmail && (
              <div className="d-flex align-items-center">
                <PsCheckboxTuner
                  inputId="no-email"
                  label={t('primaryEmail')}
                  name="no-email"
                  disabled
                  extraClass="mr-4"
                />
                <div className="body1 ml-4 pt-3 mb-0">
                  <PSBannerWrapper
                    message={t('noEmailProvided')}
                    level="info"
                    icon
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = () => {
    if (isEmailSent) {
      resetState();
      return;
    }
    if (hasValidEmail) {
      sendConfirmationEmail({
        enrollment_type: enrollmentContext,
        org_plan_year_code: orgPlanYearCode,
        life_event_completed: false,
        recipients: emailRecipients,
      });
      setIsEmailSent(true);
    } else {
      const body = document.querySelector('body');
      body?.classList.remove('modal-open');
      body?.removeAttribute('style');
      const modalBackdrop = document.querySelector('.modal-backdrop');
      modalBackdrop?.remove();
      // user has no emails, must configure
      navigate(`/enrollment/${ROUTES.enrollment.profile.path}`);
    }
  };

  const resetState = () => {
    setIsEmailSent(false);
    setEmailRecipients([null, null]);
  };

  const primaryButtonText = () => {
    if (isEmailSent) {
      return t('close');
    } else if (hasValidEmail) {
      return t('send');
    } else {
      return t('goToProfile');
    }
  };

  if (!hasValidEmail && !canUpdateEmail) {
    return null;
  }

  return (
    <>
      <ps-button
        id="email-statement"
        level="tertiary"
        label={t('common:email')}
        icon-right="envelope-simple"
        other-data="data-bs-toggle='modal' data-bs-target='#email-statement-modal'"
        data-testid="email-confirmation-button"
      ></ps-button>
      <PsModalTuner
        data-testid="email-confirmation-modal"
        modalId="email-statement-modal"
        modalTitle={t('title')}
        primaryActive={!isEmailSent}
        primaryButton={primaryButtonText()}
        secondaryButton={isEmailSent ? undefined : 'Cancel'}
        onPsModalSubmit={() => handleSubmit()}
        onPsModalDismiss={resetState}
        disablePrimary={!enableSubmit}
        disablePrimaryDismiss={!isEmailSent}
        extraClass="zindex-modal"
      >
        {renderModalBody()}
      </PsModalTuner>
    </>
  );
};
